import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getLeadSubmissions } from "../api";
import { resolveIsContacted } from "../utils/isContactedProject";
import { extractContactedProjectIds } from "../utils/extractContactedProjectIds";
import { PROJECT_CONTACTED_EVENT } from "../utils/contactLeadSuccess";

const ContactedProjectsContext = createContext(null);

function readAuthToken() {
  try {
    return localStorage.getItem("auth_token");
  } catch {
    return null;
  }
}

export function ContactedProjectsProvider({ children }) {
  const [contactedOverrideIds, setContactedOverrideIds] = useState(() => new Set());
  const [contactedCountByProjectId, setContactedCountByProjectId] = useState(() => new Map());

  const markContacted = useCallback((projectId) => {
    const id = projectId != null ? String(projectId).trim() : "";
    if (!id) return;
    setContactedOverrideIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setContactedCountByProjectId((prev) => {
      const next = new Map(prev);
      next.set(id, (next.get(id) ?? 0) + 1);
      return next;
    });
  }, []);

  const clearContacted = useCallback(() => {
    setContactedOverrideIds(new Set());
    setContactedCountByProjectId(new Map());
  }, []);

  const isContacted = useCallback(
    (item) => resolveIsContacted(item, contactedOverrideIds),
    [contactedOverrideIds],
  );

  const getContactedCount = useCallback(
    (itemOrId) => {
      const id =
        itemOrId != null && typeof itemOrId === "object"
          ? String(itemOrId?.project?.id ?? itemOrId?.id ?? itemOrId?.project_id ?? "").trim()
          : String(itemOrId ?? "").trim();
      if (!id) return 0;
      const fromMap = contactedCountByProjectId.get(id);
      if (fromMap != null && fromMap > 0) return fromMap;
      if (contactedOverrideIds.has(id)) return 1;
      if (itemOrId && typeof itemOrId === "object") {
        if (itemOrId.is_contacted === true || itemOrId.isContacted === true) return 1;
      }
      return 0;
    },
    [contactedCountByProjectId, contactedOverrideIds],
  );

  const syncFromLeadSubmissions = useCallback(async () => {
    const token = readAuthToken();
    if (!token) {
      clearContacted();
      return;
    }
    try {
      const res = await getLeadSubmissions({});
      const ids = extractContactedProjectIds(res);
      setContactedOverrideIds(ids);
      const countMap = new Map();
      ids.forEach((id) => countMap.set(id, 1));
      setContactedCountByProjectId(countMap);
    } catch {
      /* keep existing set on transient errors */
    }
  }, [clearContacted]);

  useEffect(() => {
    const onLeadSuccess = (event) => {
      const id = event?.detail?.projectId;
      if (id) markContacted(id);
    };
    window.addEventListener(PROJECT_CONTACTED_EVENT, onLeadSuccess);
    return () => window.removeEventListener(PROJECT_CONTACTED_EVENT, onLeadSuccess);
  }, [markContacted]);

  useEffect(() => {
    const onAuthChanged = () => {
      if (!readAuthToken()) {
        clearContacted();
        return;
      }
      syncFromLeadSubmissions();
    };
    window.addEventListener("auth-changed", onAuthChanged);
    return () => window.removeEventListener("auth-changed", onAuthChanged);
  }, [clearContacted, syncFromLeadSubmissions]);

  // Hydrate on mount when user is already logged in
  useEffect(() => {
    if (readAuthToken()) {
      syncFromLeadSubmissions();
    }
  }, [syncFromLeadSubmissions]);

  const value = useMemo(
    () => ({
      contactedOverrideIds,
      contactedCountByProjectId,
      markContacted,
      clearContacted,
      isContacted,
      getContactedCount,
      syncFromLeadSubmissions,
    }),
    [
      contactedOverrideIds,
      contactedCountByProjectId,
      markContacted,
      clearContacted,
      isContacted,
      getContactedCount,
      syncFromLeadSubmissions,
    ],
  );

  return (
    <ContactedProjectsContext.Provider value={value}>
      {children}
    </ContactedProjectsContext.Provider>
  );
}

export function useContactedProjects() {
  const ctx = useContext(ContactedProjectsContext);
  if (!ctx) {
    throw new Error("useContactedProjects must be used within ContactedProjectsProvider");
  }
  return ctx;
}

export default ContactedProjectsContext;
